import type { CanonicalPresentationModelV1, CanonicalPrimaryAction } from "@enrein/shared-contracts";
import type { CommandCenterStatCard } from "../../studio-shared/orchestration/project-phase.js";
import type { V10QueueCard } from "../adapters/v10-fixtures.js";
import { v10NewWorkSheet, v10ProjectRoute, v10ViewRoute } from "../adapters/v10-button-endpoints.js";
import { V10QueueSection } from "../components/V10QueueCard.js";
import { V10StartHereCard } from "../components/V10StartHereCard.js";
import { V10TodayEntryButtons } from "../components/V10TodayEntryButtons.js";
import { V10Kicker } from "../components/V10Primitives.js";
import type { V10Navigate } from "../routing/v10-routing.js";
import type { V10QueueSections } from "../V10Shell.js";

export function V10CommandCenterView({
  onNavigate,
  onOpenProject,
  queueSections,
  portfolioSignals,
  portfolioLoading,
  portfolioError,
  onStatNavigate,
  startHere = null,
  onStartHereAction,
}: {
  readonly onNavigate: V10Navigate;
  readonly onOpenProject: V10Navigate;
  readonly queueSections: V10QueueSections;
  readonly portfolioSignals: readonly { readonly label: string; readonly value: string; readonly navigateTo: CommandCenterStatCard["navigateTo"] }[];
  readonly portfolioLoading: boolean;
  readonly portfolioError: string | null;
  readonly onStatNavigate: (target: NonNullable<CommandCenterStatCard["navigateTo"]>) => void;
  readonly startHere?: CanonicalPresentationModelV1 | null;
  readonly onStartHereAction?: (action: CanonicalPrimaryAction) => void;
}) {
  const openQueue = (card: V10QueueCard) => {
    onOpenProject(v10ProjectRoute({ projectId: card.projectId, section: card.targetSection, mode: card.mode }));
  };

  return (
    <section className="v10-screen" aria-label="Today">
      <div className="v10-hero v10-hero--topline">
        <div>
          <V10Kicker>Today</V10Kicker>
          <h1 className="v10-display-title v10-display-title--paper">What needs your attention right now?</h1>
          <p className="v10-lede v10-lede--paper">
            Start new work, continue projects, review client requests, and clear blockers — one next
            click at a time.
          </p>
          {startHere ? (
            <V10StartHereCard
              model={startHere}
              onPrimaryAction={(action) => {
                onStartHereAction?.(action);
              }}
            />
          ) : null}
          <div className={startHere ? "v10-today-entries-wrap v10-today-entries-wrap--secondary" : undefined}>
            <V10TodayEntryButtons
              onNewWebsite={() => onNavigate(v10NewWorkSheet("DEFAULT_GENERATION"))}
              onRelaunch={() => onNavigate(v10ViewRoute("scans", "relaunch"))}
              onExternalProof={() => onNavigate(v10ViewRoute("scans", "external-proof"))}
            />
          </div>
        </div>
        <aside className="v10-hero-card v10-hero-card--paper" aria-label="Workspace signals">
          <details className="v10-signals-rail" open={portfolioSignals.length > 0}>
            <summary>Workspace signals (secondary)</summary>
            {portfolioError ? <p className="v10-validation">{portfolioError}</p> : null}
            <div className="v10-metric-grid v10-metric-grid--compact" aria-busy={portfolioLoading}>
              {portfolioSignals.map((metric) => (
                <button
                  key={metric.label}
                  type="button"
                  className="v10-metric-card v10-metric-card--clickable"
                  disabled={!metric.navigateTo || portfolioLoading}
                  onClick={() => {
                    if (metric.navigateTo) onStatNavigate(metric.navigateTo);
                  }}
                >
                  <b>{portfolioLoading ? "—" : metric.value}</b>
                  <span>{metric.label}</span>
                </button>
              ))}
            </div>
          </details>
        </aside>
      </div>

      <V10QueueSection title="Continue where you left off" cards={queueSections.continueCards} onAction={openQueue} />
      <V10QueueSection title="Needs attention" cards={queueSections.attentionCards} onAction={openQueue} />
      <V10QueueSection title="Client requests" cards={queueSections.clientRequestCards} onAction={openQueue} />
      <V10QueueSection title="Stuck / blocked" cards={queueSections.stuckCards} onAction={openQueue} />
      <V10QueueSection title="Ready for handoff" cards={queueSections.readyCards} onAction={openQueue} />
    </section>
  );
}
